// src/services/supervisorService.js
import { supabase } from './supabaseClient';

export const supervisorService = {
  /**
   * Assign a supervisee to a supervisor
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} superviseeId - Supervisee's UUID
   * @param {string} createdBy - Admin/Manager's UUID making the assignment
   * @returns {Promise<void>}
   */
  async assignSupervisee(supervisorId, superviseeId, createdBy) {
    try {
      const { error } = await supabase
        .from('supervisor_assignments')
        .insert([
          {
            supervisor_id: supervisorId,
            supervisee_id: superviseeId,
            can_edit_submissions: false,
            created_by: createdBy,
          },
        ]);

      if (error && error.code !== '23505') {
        // 23505 is unique constraint (already assigned)
        throw error;
      }
    } catch (err) {
      console.error('Error assigning supervisee:', err);
      throw err;
    }
  },

  /**
   * Remove a supervisee from a supervisor
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} superviseeId - Supervisee's UUID
   * @returns {Promise<void>}
   */
  async removeSupervisee(supervisorId, superviseeId) {
    try {
      const { error } = await supabase
        .from('supervisor_assignments')
        .delete()
        .eq('supervisor_id', supervisorId)
        .eq('supervisee_id', superviseeId);

      if (error) throw error;
    } catch (err) {
      console.error('Error removing supervisee:', err);
      throw err;
    }
  },

  /**
   * Enable edit permission for supervisee
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} superviseeId - Supervisee's UUID
   * @returns {Promise<void>}
   */
  async enableEditPermission(supervisorId, superviseeId) {
    try {
      const { error } = await supabase
        .from('supervisor_assignments')
        .update({ can_edit_submissions: true })
        .eq('supervisor_id', supervisorId)
        .eq('supervisee_id', superviseeId);

      if (error) throw error;
    } catch (err) {
      console.error('Error enabling edit permission:', err);
      throw err;
    }
  },

  /**
   * Disable edit permission for supervisee
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} superviseeId - Supervisee's UUID
   * @returns {Promise<void>}
   */
  async disableEditPermission(supervisorId, superviseeId) {
    try {
      const { error } = await supabase
        .from('supervisor_assignments')
        .update({ can_edit_submissions: false })
        .eq('supervisor_id', supervisorId)
        .eq('supervisee_id', superviseeId);

      if (error) throw error;
    } catch (err) {
      console.error('Error disabling edit permission:', err);
      throw err;
    }
  },

  /**
   * Assign country to supervisor
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} countryId - Country's UUID
   * @param {string} assignedBy - Admin/Manager's UUID making the assignment
   * @returns {Promise<void>}
   */
  async assignCountry(supervisorId, countryId, assignedBy) {
    try {
      const { error } = await supabase
        .from('user_countries')
        .insert([
          {
            user_id: supervisorId,
            country_id: countryId,
            assigned_by: assignedBy,
          },
        ]);

      if (error && error.code !== '23505') {
        // 23505 is unique constraint (already assigned)
        throw error;
      }
    } catch (err) {
      console.error('Error assigning country:', err);
      throw err;
    }
  },

  /**
   * Remove country from supervisor
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} countryId - Country's UUID
   * @returns {Promise<void>}
   */
  async removeCountry(supervisorId, countryId) {
    try {
      const { error } = await supabase
        .from('user_countries')
        .delete()
        .eq('user_id', supervisorId)
        .eq('country_id', countryId);

      if (error) throw error;
    } catch (err) {
      console.error('Error removing country:', err);
      throw err;
    }
  },

  /**
   * Get all countries assigned to supervisor
   * @param {string} supervisorId - Supervisor's UUID
   * @returns {Promise<Array<object>>}
   */
  async getSupervisorCountries(supervisorId) {
    try {
      const { data, error } = await supabase
        .from('user_countries')
        .select('countries(id, name, code)')
        .eq('user_id', supervisorId);

      if (error) throw error;
      return data.map(uc => uc.countries);
    } catch (err) {
      console.error('Error fetching supervisor countries:', err);
      return [];
    }
  },
};
